import SpeakButton from "../EnglishTool/SpeakButton";

export const chapterPages = {
  1: [
    {
      title: <h2>Grade 7 Nouns: Abstract, Compound, Noun Clauses</h2>,
      body: (
        <>
          <p>
            <b>Abstract nouns</b> express ideas, qualities, or emotions, not physical things.
            <br />
            Example: <i><b>Democracy, ambition, nostalgia</b></i>
            <SpeakButton text="Democracy, ambition, nostalgia" />
          </p>
          <p>
            <b>Compound nouns</b> are formed by joining two or more words.<br />
            Examples: <i><b>father-in-law, high school, toothpaste</b></i>
            <SpeakButton text="father-in-law, high school, toothpaste" />
          </p>
          <p>
            <b>Noun clauses</b> are dependent clauses that act as nouns.<br />
            Example: <i><b>Whatever you decide</b> will influence the outcome.</i>
            <SpeakButton text="Whatever you decide will influence the outcome." />
          </p>
          <p>
            <b>Functions of nouns:</b> Subject, direct/indirect object, predicate noun, object of preposition, appositive.
            <br />
            Example: <i>
              <b>The teacher</b> (subject) gave <b>her students</b> (indirect object) <b>advice</b> (direct object) in the <b>classroom</b> (object of preposition).
            </i>
            <SpeakButton text="The teacher gave her students advice in the classroom." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Gerunds, Infinitives, and Appositives in Academic Writing</h2>,
      body: (
        <>
          <p>
            <b>Gerund:</b> Verb + “-ing” used as a noun.
            <br />
            Example: <i><b>Writing</b> improves your thinking.</i>
            <SpeakButton text="Writing improves your thinking." />
          </p>
          <p>
            <b>Infinitive:</b> “to” + verb, often used as a noun.
            <br />
            Example: <i><b>To explore</b> new ideas is essential in science.</i>
            <SpeakButton text="To explore new ideas is essential in science." />
          </p>
          <p>
            <b>Appositive:</b> A noun or noun phrase that renames another noun.
            <br />
            Example: <i>
              Dr. James, <b>a renowned scientist</b>, will speak today.
            </i>
            <SpeakButton text="Dr. James, a renowned scientist, will speak today." />
          </p>
          <p>
            <b>Academic Example:</b> <i><b>Reading</b> challenging books helps you become <b>a critical thinker</b>, a skill necessary for success.</i>
            <SpeakButton text="Reading challenging books helps you become a critical thinker, a skill necessary for success." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Advanced Noun Applications</h2>,
      body: (
        <>
          <p>
            <b>Identify:</b> Find the gerund and the appositive phrase in this sentence:
            <br />
            <b>
              "Organizing events, <b>a demanding task</b>, requires patience."
              <SpeakButton text="Organizing events, a demanding task, requires patience." />
            </b>
          </p>
          <p>
            <b>Challenge:</b> Write a sentence that uses a noun clause as the subject and a compound noun.
            <br />
            <i>Example: <b>What you contribute</b> shapes the <b>team spirit</b>.</i>
            <SpeakButton text="What you contribute shapes the team spirit." />
          </p>
        </>
      ),
    },
  ],
  2: [
    {
      title: <h2>Grade 7 Verbs: Mood, Voice, and Modal Auxiliaries</h2>,
      body: (
        <>
          <p>
            <b>Verb Mood:</b> Conveys intention or emotion.<br />
            <b>Indicative:</b> She <b>studies</b> daily.<br />
            <b>Imperative:</b> <b>Study</b> for your test.<br />
            <b>Subjunctive:</b> If she <b>were</b> here, she <b>would help</b>.
            <br />
            <SpeakButton text="She studies daily. Study for your test. If she were here, she would help." />
          </p>
          <p>
            <b>Voice:</b> Shows whether the subject acts or is acted upon.
            <br />
            Active: <i>The author <b>wrote</b> the novel.</i>
            <br />
            Passive: <i>The novel <b>was written</b> by the author.</i>
            <SpeakButton text="The author wrote the novel. The novel was written by the author." />
          </p>
          <p>
            <b>Modal Auxiliaries:</b> can, could, may, might, must, shall, should, will, would.
            <br />
            Example: <i>Students <b>must</b> complete the assignment on time.</i>
            <SpeakButton text="Students must complete the assignment on time." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Perfect, Progressive, and Emphatic Tenses (Advanced)</h2>,
      body: (
        <>
          <p>
            <b>Perfect tenses:</b> Action completed before another point in time.
            <br />
            Example: <i>By the time he arrived, they <b>had left</b>.</i>
            <SpeakButton text="By the time he arrived, they had left." />
          </p>
          <p>
            <b>Progressive tenses:</b> Ongoing action.
            <br />
            Example: <i>She <b>was reading</b> when I called.</i>
            <SpeakButton text="She was reading when I called." />
          </p>
          <p>
            <b>Emphatic tense:</b> Uses “do/does/did” for emphasis.
            <br />
            Example: <i>I <b>do</b> believe you can succeed.</i>
            <SpeakButton text="I do believe you can succeed." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Challenging Verb Forms</h2>,
      body: (
        <>
          <p>
            <b>Transform:</b> Change this to passive voice:
            <br />
            "The engineers solved the difficult problem."
            <SpeakButton text="The engineers solved the difficult problem." />
          </p>
          <p>
            <b>Construct:</b> Write a sentence using both a modal and a perfect tense.
            <br />
            <i>Example: She <b>must have finished</b> her project by now.</i>
            <SpeakButton text="She must have finished her project by now." />
          </p>
        </>
      ),
    },
  ],
  3: [
    {
      title: <h2>Grade 7 Adjectives: Order, Clauses, and Absolutes</h2>,
      body: (
        <>
          <p>
            <b>Order of adjectives:</b> (Opinion, size, age, shape, color, origin, material, purpose)
            <br />
            Example: <i>a beautiful large old round blue Italian marble table</i>
            <SpeakButton text="a beautiful large old round blue Italian marble table" />
          </p>
          <p>
            <b>Restrictive vs. Non-restrictive Adjective Clauses:</b>
            <br />
            Restrictive: <i>The person <b>who called</b> left a message.</i><br />
            Non-restrictive: <i>Mr. Li, <b>who is our neighbor</b>, is a doctor.</i>
            <SpeakButton text="Mr. Li, who is our neighbor, is a doctor." />
          </p>
          <p>
            <b>Absolute Phrase:</b> A phrase modifying the whole clause.
            <br />
            Example: <i>
              <b>The sun having set</b>, we began our journey.
            </i>
            <SpeakButton text="The sun having set, we began our journey." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Participial Adjectives & Advanced Modifiers</h2>,
      body: (
        <>
          <p>
            <b>Participial adjectives:</b> Adjectives formed from verbs.<br />
            Example: <i>The <b>exciting</b> news spread quickly.</i>
            <SpeakButton text="The exciting news spread quickly." />
          </p>
          <p>
            <b>Stacking modifiers:</b> Using multiple adjectives and phrases for academic description.<br />
            Example: <i>
              The <b>well-written, thought-provoking, internationally acclaimed</b> novel won many awards.
            </i>
            <SpeakButton text="The well-written, thought-provoking, internationally acclaimed novel won many awards." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Academic Adjective Use</h2>,
      body: (
        <>
          <p>
            <b>Identify:</b> Find the participial adjective and the absolute phrase:<br />
            <b>
              Exhausted by the hike, Lina sat down, her energy spent.
              <SpeakButton text="Exhausted by the hike, Lina sat down, her energy spent." />
            </b>
          </p>
          <p>
            <b>Challenge:</b> Write a sentence using at least three stacked adjectives before the noun, and include an adjective clause.
            <br />
            <i>Example: The <b>ancient, mysterious, overgrown</b> garden <b>that I visited</b> fascinated me.</i>
            <SpeakButton text="The ancient, mysterious, overgrown garden that I visited fascinated me." />
          </p>
        </>
      ),
    },
  ],
  4: [
    {
      title: <h2>Grade 7 Adverbs: Clauses and Conjunctive Adverbs</h2>,
      body: (
        <>
          <p>
            <b>Adverb clauses:</b> Begin with <i>because, although, unless, since, when, after, while</i> and modify verbs, adjectives, or adverbs.<br />
            Example: <i>She stayed inside <b>because it was raining</b>.</i>
            <SpeakButton text="She stayed inside because it was raining." />
          </p>
          <p>
            <b>Conjunctive adverbs:</b> Connect independent clauses; show relationships.<br />
            Examples: <i>however, therefore, moreover, consequently, meanwhile</i>
            <br />
            Example: <i>He was late; <b>however</b>, he finished his work.</i>
            <SpeakButton text="He was late; however, he finished his work." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Comparative/Superlative and Placement</h2>,
      body: (
        <>
          <p>
            <b>Comparative adverbs:</b> Show difference.<br />
            Example: <i>She answered <b>more confidently</b> than before.</i>
            <SpeakButton text="She answered more confidently than before." />
          </p>
          <p>
            <b>Superlative adverbs:</b> Show the extreme degree.<br />
            Example: <i>Of all the students, he solved the problems <b>most rapidly</b>.</i>
            <SpeakButton text="Of all the students, he solved the problems most rapidly." />
          </p>
          <p>
            <b>Adverb placement:</b> Adverbs can move for emphasis or clarity.<br />
            Examples: <i>
              Suddenly, the lights went out. <br />
              The lights suddenly went out. <br />
              The lights went out suddenly.
            </i>
            <SpeakButton text="Suddenly, the lights went out. The lights suddenly went out. The lights went out suddenly." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Complex Adverb Use</h2>,
      body: (
        <>
          <p>
            <b>Combine:</b> Join the sentences using a conjunctive adverb:
            <br />
            <b>
              The test was difficult. Most students passed.
              <SpeakButton text="The test was difficult. Most students passed." />
            </b>
          </p>
          <p>
            <b>Create:</b> Write a sentence with an adverb clause (starting with "while") and a superlative adverb.
            <br />
            <i>Example: While others hesitated, she answered most confidently.</i>
            <SpeakButton text="While others hesitated, she answered most confidently." />
          </p>
        </>
      ),
    },
  ],
  5: [
    {
      title: <h2>Grade 7 Reading: Inference, Perspective, and Tone</h2>,
      body: (
        <>
          <p>
            <b>Inference:</b> Draw logical conclusions from textual clues.<br />
            Example: <i>
              “Despite the thunder, Maya continued reading, undisturbed.”
            </i>
            <SpeakButton text="Despite the thunder, Maya continued reading, undisturbed." />
          </p>
          <p>
            <b>Author’s Perspective & Tone:</b> Analyze language, details, and structure to understand the writer’s attitude.
            <br />
            Example: <i>
              “He described the scene with a sense of awe and nostalgia.”
            </i>
            <SpeakButton text="He described the scene with a sense of awe and nostalgia." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Advanced Analysis: Structure & Rhetorical Devices</h2>,
      body: (
        <>
          <p>
            <b>Text structures:</b> Cause/effect, problem/solution, compare/contrast, chronological.
            <br />
            Example: <i>
              The passage contrasts city life with rural life to highlight different values.
            </i>
            <SpeakButton text="The passage contrasts city life with rural life to highlight different values." />
          </p>
          <p>
            <b>Rhetorical Devices:</b> Metaphor, simile, repetition, rhetorical question, hyperbole.
            <br />
            Example: <i>
              “Her ideas spread like wildfire” (simile)
            </i>
            <SpeakButton text="Her ideas spread like wildfire." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Analytical Reading</h2>,
      body: (
        <>
          <p>
            <b>Analyze:</b> What is the author’s tone and what can you infer?<br />
            <i>
              “The empty auditorium echoed with memories of applause.”
            </i>
            <SpeakButton text="The empty auditorium echoed with memories of applause." />
          </p>
          <p>
            <b>Respond:</b> Write a paragraph explaining the main idea and structure of a challenging article or essay you have read.
          </p>
        </>
      ),
    },
  ],
};
